import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { ConfirmModal } from '~/components/ConfirmModal';
import { Caption, FsButton, FsIconButton, Heading, LoadingSwitch, Text } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { CheckIcon, CopyToClipboardIcon, ErrorIcon, PencilIcon, WarningIcon } from '~/components/icons';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type { StripeConfigurationResponse, StripeEventDestinationStatus } from '~/server_shared/openapi/data-contracts';
import colors from '~/theme/tailwind/colors';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import type { StripeConfigurationFormValues } from './ConfigurationFormDialog';
import StripeSetupDialog, { STRIPE_RAIL_DISPLAY_NAMES } from './ConfigurationFormDialog';

const CopyDetailButton: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <FsIconButton
    icon={CopyToClipboardIcon}
    aria-label={`Copy ${label}`}
    onClick={() => {
      void navigator.clipboard.writeText(text);
    }}
  />
);

// No `isSecret` variant here (unlike the Vitesse screen's Detail): the Stripe config response never
// carries the secret key or the webhook signing secret, so there is no secret to mask or reveal.
//
// `isCopyable` exists for the opaque provider identifiers (`ed_test_…`, `fa_test_…`) and the absolute
// webhook URL. They are 40+ unbreakable characters that nobody can retype into the Stripe Dashboard
// accurately, which is the only reason to read them off this screen at all.
const Detail: React.FC<{ label: string; text: string; isCopyable?: boolean; className?: string }> = ({
  label,
  text,
  isCopyable = false,
  className,
}) => (
  <div className={cn('min-w-40', className)}>
    <Caption variant={Caption.VARIANTS.LABEL}>{label}</Caption>
    <div className="flex items-start">
      {/* `overflow-wrap` is inherited, so `wrap-anywhere` goes on the text itself: these values are
          single unbroken tokens, and without it they overflow their flex item and collide with the
          next column rather than wrapping inside it. */}
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="text-slate-900 wrap-anywhere">
        {text}
      </Text>
      {isCopyable && text ? <CopyDetailButton label={label} text={text} /> : null}
    </div>
  </div>
);

const containerWithBorderCn = 'border border-solid border-slate-600 rounded-md p-24 mt-20 w-full';
const sectionTitleCn = 'flex items-center';
// `gap-24` rather than a margin on each Detail, so the last column has no trailing gutter and a
// wrapped row keeps the same spacing as an unwrapped one.
const sectionDetailsCn = 'mt-12 flex flex-wrap gap-24';

const RE_REGISTER_CONFIRM_TITLE = 'Re-register event destination';
// Distinct from the section's own 'Re-register' button, so which one a click lands on is never
// ambiguous - to a screen reader as much as to a test.
const RE_REGISTER_CONFIRM_BUTTON = 'Delete and re-register';

/**
 * The event types this deployment expects, each marked present or missing, followed by anything Stripe
 * is sending that this deployment did not ask for.
 *
 * One row per expected type rather than a joined line, because the question an operator brings here is
 * "is `…returned` subscribed?" and a comma-separated sentence makes that a reading exercise. A missing
 * type is the failure: every event of it is dropped in silence, and no payout that ends that way ever
 * leaves `submitted`.
 *
 * Unexpected types are listed too, and deliberately subordinate - shown, never flagged. Nothing is
 * broken by one: everything this deployment handles still arrives, and the extra is fetched and
 * dropped, which costs a call and no more. The reason to show it is that it is evidence - this
 * deployment never registers a type it does not handle, so one being here means the destination was
 * edited by hand or belongs to another deployment. The reason not to raise it as a fault is that the
 * edit may have been deliberate, and the only repair on offer, Re-register, would delete it.
 *
 * The expected set is the server's, never restated here: a second copy in the frontend would drift from
 * the handler's own set the first time an event type is added, which is the drift this list exists to
 * catch.
 */
const EventSubscriptionList: React.FC<{ enabledEvents: string[]; expectedEvents: string[] }> = ({
  enabledEvents,
  expectedEvents,
}) => {
  const registered = new Set(enabledEvents);
  const expected = new Set(expectedEvents);
  const unexpectedEvents = enabledEvents.filter((eventType) => !expected.has(eventType));

  return (
    <div className="mt-12">
      <Caption variant={Caption.VARIANTS.LABEL}>{`Subscribed Events (${enabledEvents.length})`}</Caption>
      <ul className="m-0 list-none p-0">
        {expectedEvents.map((eventType) => {
          const isPresent = registered.has(eventType);
          return (
            <li key={eventType} className="mt-4 flex items-center gap-8">
              {isPresent ? <CheckIcon iconColor={colors.green['700']} /> : <ErrorIcon iconColor={colors.red['700']} />}
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="text-slate-900 wrap-anywhere">
                {eventType}
              </Text>
              {isPresent ? null : (
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="text-slate-900">
                  not subscribed
                </Text>
              )}
            </li>
          );
        })}
      </ul>
      {unexpectedEvents.length > 0 ? (
        <div className="mt-12">
          <Caption variant={Caption.VARIANTS.LABEL}>Not expected by CMS</Caption>
          <ul className="m-0 list-none p-0">
            {unexpectedEvents.map((eventType) => (
              <li key={eventType} className="mt-4 flex items-center gap-8">
                <WarningIcon iconColor={colors.yellow['800']} />
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="text-slate-900 wrap-anywhere">
                  {eventType}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * The destination's health, said out loud in every state - including the healthy one.
 *
 * Without an affirmative "Active", a working integration and one nobody has ever registered read the
 * same way: an absence of complaint. Stating it is the cheapest defence against the silent failure
 * this whole screen exists to surface.
 *
 * Text stays `slate-900` on the tinted backgrounds rather than a matching mid-tone, which is a
 * readability floor this codebase holds to generally.
 */
const DestinationHealthChip: React.FC<{
  isRegistered: boolean;
  isUrlCurrent: boolean;
  areExpectedEventsSubscribed: boolean;
}> = ({ isRegistered, isUrlCurrent, areExpectedEventsSubscribed }) => {
  if (!isRegistered) {
    return <Chip size="small" label="Not registered" className="ml-12 bg-slate-200 text-slate-900" />;
  }

  // Named rather than collapsed into one "Needs attention": the two faults have different causes and
  // the label is the first thing read, so it may as well say which one to go and look at.
  // Only a *missing* expected type counts as a fault. A type Stripe sends that CMS does not handle is
  // listed below but never reddens this: everything CMS handles still arrives, and re-registering to
  // "fix" it would delete a subscription somebody may have added on purpose.
  const faults = [!isUrlCurrent ? 'URL' : null, !areExpectedEventsSubscribed ? 'Events' : null].filter(Boolean);
  if (faults.length === 0) {
    return <Chip size="small" label="Active" className="ml-12 bg-green-100 text-slate-900" />;
  }

  return (
    <Chip size="small" label={`${faults.join(' & ')} mismatch`} className={cn('ml-12', 'bg-red-100 text-slate-900')} />
  );
};

/**
 * Where Stripe delivers this organization's events, and whether that is still here.
 *
 * Mirrors the Vitesse screen's PayeeChoiceWebhookSection, with three deliberate differences, each
 * because Stripe's destination is not Vitesse's webhook:
 *
 * - Re-register is confirmed, and stays visually quiet while the destination is healthy. Vitesse's
 *   Register only ever adds; this deletes a live destination and rotates its signing secret, and
 *   events already queued for retry to the old one are lost. A destructive action should not be the
 *   loudest thing on a card that is reporting everything is fine.
 * - The stale-URL state is stated in words, flagged by the header chip, and promotes the button to
 *   filled `secondary`. It is the one case where the section says "registered" and the integration is
 *   nonetheless dropping every event. Note that `secondary` is this theme's loud crimson and `primary`
 *   its muted navy, so urgency reads as secondary-over-outlined, not primary-over-secondary.
 * - No signing secret is shown. It is machine-written and never leaves the backend, which is the same
 *   note the Stripe Details block above already carries about the rest of the configuration.
 */
const EventDestinationSection: React.FC<{ organizationId: number | string }> = ({ organizationId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReRegisterOpen, setIsReRegisterOpen] = useState(false);
  const url = `/sysconfig/api/v1/organizations/${organizationId}/stripe/event_destination`;
  const { isLoading, isError, data, reloadData } = useDataFetcher<StripeEventDestinationStatus>(url);

  const submit = async (path: string) => {
    setIsSubmitting(true);
    try {
      await axios.post(path);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
      setIsReRegisterOpen(false);
    }
  };

  return (
    <div className={containerWithBorderCn}>
      <div className={sectionTitleCn}>
        <Heading variant={Heading.TYPES.H4}>Event Destination</Heading>
        {data ? (
          <DestinationHealthChip
            isRegistered={data.is_registered}
            isUrlCurrent={Boolean(data.is_url_current)}
            areExpectedEventsSubscribed={Boolean(data.are_expected_events_subscribed)}
          />
        ) : null}
      </div>
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        {data?.is_registered ? (
          <>
            <div className={sectionDetailsCn}>
              <Detail label="Destination ID" text={data.event_destination_id || ''} isCopyable className="basis-1/3" />
              <Detail label="URL" text={data.url || ''} isCopyable className="basis-1/2" />
            </div>
            <EventSubscriptionList
              enabledEvents={data.enabled_events ?? []}
              expectedEvents={data.expected_events ?? []}
            />
            {!data.is_url_current ? (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mt-12 text-slate-900">
                Registered URL does not match this environment — Stripe events are not reaching CMS. Re-register to
                correct it.
              </Text>
            ) : null}
            {!data.are_expected_events_subscribed ? (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mt-12 text-slate-900">
                Stripe is not subscribed to every event this environment handles — the ones marked above never arrive.
                Re-register to correct it.
              </Text>
            ) : null}
            <div className={sectionDetailsCn}>
              <FsButton
                color="secondary"
                variant={data.is_url_current && data.are_expected_events_subscribed ? 'outlined' : 'contained'}
                disabled={isSubmitting}
                onClick={() => {
                  setIsReRegisterOpen(true);
                }}
              >
                Re-register
              </FsButton>
            </div>
          </>
        ) : (
          <div className={sectionDetailsCn}>
            <FsButton color="primary" variant="contained" disabled={isSubmitting} onClick={() => submit(url)}>
              Register
            </FsButton>
          </div>
        )}
      </LoadingSwitch>
      {isReRegisterOpen ? (
        <ConfirmModal
          title={RE_REGISTER_CONFIRM_TITLE}
          contentText="This deletes the current destination at Stripe and creates a new one with a new signing secret. Events queued for retry to the old destination are lost."
          primaryButtonName={RE_REGISTER_CONFIRM_BUTTON}
          isOpen
          isSubmitting={isSubmitting}
          onClose={() => {
            setIsReRegisterOpen(false);
          }}
          onPrimaryBtnClick={() => submit(`${url}/re_register`)}
        />
      ) : null}
    </div>
  );
};

const InternalAdminStripe: React.FC = () => {
  const [configurationFormOpen, setConfigurationFormOpen] = useState(false);
  const { organization } = useSysconfig() as { organization: { id: number } };
  const configUrl = `/sysconfig/api/v1/organizations/${organization.id}/stripe/config`;
  const { isLoading, isError, data, reloadData } = useDataFetcher<StripeConfigurationResponse>(configUrl);

  const stripeConfiguration = data?.configuration;

  const handleSubmitStripeConfiguration = async (values: StripeConfigurationFormValues) => {
    try {
      await axios.put(configUrl, values);
      await reloadData();
      setConfigurationFormOpen(false);
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <CardDialog noCardTitle>
        {stripeConfiguration ? (
          <>
            <div className={containerWithBorderCn}>
              <div className={sectionTitleCn}>
                <Heading variant={Heading.TYPES.H4}>Stripe Details</Heading>
                <FsIconButton
                  icon={PencilIcon}
                  aria-label="Edit Stripe integration details"
                  onClick={() => {
                    setConfigurationFormOpen(true);
                  }}
                />
              </div>
              {/* No webhook URL here. The Event Destination section below states the absolute URL that
                  Stripe actually holds, checked against this environment; a second, relative rendering of
                  the same path next to it read as a separate endpoint. It was worth showing back when the
                  destination had to be created by hand in the Dashboard from this path - it no longer does. */}
              <div className={sectionDetailsCn}>
                <Detail
                  label="Financial Account ID"
                  text={stripeConfiguration.financial_account_external_id}
                  isCopyable
                />
                <Detail label="Financial Account Currency" text={stripeConfiguration.financial_account_currency} />
                <Detail label="Statement Descriptor" text={stripeConfiguration.statement_descriptor || ''} />
                <Detail
                  label="Enabled Rails"
                  text={stripeConfiguration.enabled_rails.map((rail) => STRIPE_RAIL_DISPLAY_NAMES[rail]).join(', ')}
                />
                <Detail label="Status" text={stripeConfiguration.is_enabled ? 'Enabled' : 'Disabled'} />
              </div>
            </div>
            <EventDestinationSection organizationId={organization.id} />
          </>
        ) : (
          <FsButton
            color="primary"
            variant="contained"
            className="mb-40 mt-40"
            onClick={() => {
              setConfigurationFormOpen(true);
            }}
          >
            Enable Stripe Integration
          </FsButton>
        )}
      </CardDialog>
      {configurationFormOpen ? (
        <StripeSetupDialog
          onSubmit={handleSubmitStripeConfiguration}
          configuration={stripeConfiguration}
          onClose={() => {
            setConfigurationFormOpen(false);
          }}
        />
      ) : null}
    </LoadingSwitch>
  );
};

export default InternalAdminStripe;
